import React, { FC, useCallback, useEffect } from 'react';
import { FormikProps } from 'formik';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { FormError, PageTitle } from '@/ui-kit/components';
import { ChooseLoginModal } from '@/bo-kit/components';
import { useIsDesktop } from '@/ui-kit/hooks/useIsDesktop';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserType } from '@/bo-kit/types';
import { GoBackLink } from '@/bo-kit/components/GoBackLink';
import { Nullable } from '@/ui-kit/types';
import { useReturnUrl } from '@/bo-kit/hooks/useReturnUrl';
import { SendCodeRequest } from '@/bo-kit/transport/types/SendCodeRequest';

export const FormInner: FC<{
    formik: FormikProps<SendCodeRequest>;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage: Nullable<string>;
    roles?: Array<UserType>;
    cancelRoleChoose: () => void;
}> = ({ formik, isLoading, isError, errorMessage, roles, isSuccess, cancelRoleChoose }) => {
    const onSelectRole = useCallback(
        (userType: UserType) => {
            formik.setFieldValue('userType', userType);
            formik.handleSubmit();
        },
        [formik]
    );

    const { t } = useTranslation('bo-kit__signup');

    const isDesktop = useIsDesktop();
    const navigate = useNavigate();

    const [returnUrl] = useReturnUrl();

    useEffect(() => {
        isSuccess &&
            navigate('newpassword', {
                state: { email: formik.values.email, userType: formik.values.userType, returnUrl },
            });
    }, [formik.values.email, formik.values.userType, isSuccess, navigate, returnUrl]);

    return (
        <form onSubmit={formik.handleSubmit}>
            <Box sx={{ maxWidth: isDesktop ? 300 : 'unset' }}>
                <PageTitle>{t('Reset password')}</PageTitle>
                <Stack spacing={4}>
                    <Typography color='textSecondary' variant='body2'>
                        {t(
                            'Enter the e-mail address you used when you joined and we’ll send you instructions to reset your password'
                        )}
                    </Typography>
                    <TextField
                        sx={{ mb: 4 }}
                        fullWidth
                        type='text'
                        label={t('E-mail')}
                        name='email'
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.email}
                        error={formik.touched.email && !!formik.errors.email}
                        helperText={formik.touched.email && formik.errors.email}
                        placeholder={t('Enter your e-mail')}
                    />
                    {isError && errorMessage && <FormError text={errorMessage} />}
                    <Box width='100%'>
                        <Button
                            type='submit'
                            fullWidth
                            color='primary'
                            disabled={isLoading}
                            variant='contained'
                        >
                            {t('Send reset instruction')}
                        </Button>
                        <Box mt={1}>
                            <GoBackLink />
                        </Box>
                    </Box>
                </Stack>
            </Box>
            {roles != null && (
                <ChooseLoginModal
                    roles={roles}
                    btnCaption='Reset password as {{ROLE}}'
                    onCancel={cancelRoleChoose}
                    onSelect={onSelectRole}
                />
            )}
        </form>
    );
};
